import type { ReactNode } from 'react';
import { useLocation, useParams, useRouter } from '@tanstack/react-router';
import { getObjectTypeBySlug } from '@/services/db/object-type/entity';
import type { ObjectType } from '@/services/db/object-type/entity';
import { useObjectTypes } from '@/services/db/object-type/queries';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useRegisterBreadcrumbs } from '@/services/breadcrumbs';
import { objectTypeRedirectHref } from './urls';

type ObjectTypeRoute = '/workspaces/$workspaceId/objects/$slug' | '/workspaces/$workspaceId/objects/$slug/guide';

type ObjectTypeResolverProps = {
  /** The caller's own route, so a legacy id-based URL normalizes to the slug on the same tab. */
  route: ObjectTypeRoute;
  children: (objectType: ObjectType, workspaceId: number) => ReactNode;
};

/**
 * Resolves the `$slug` route param to an object type, registering the
 * breadcrumb trail and normalizing legacy id-based URLs to the slug. An
 * unknown slug replace-redirects to the objects index.
 */
export function ObjectTypeResolver({ route, children }: ObjectTypeResolverProps) {
  const workspaceId = requireWorkspaceId();
  const { slug } = useParams({ strict: false });
  const router = useRouter();
  const location = useLocation();
  const { data: objectTypes } = useObjectTypes({ queryConfig: { staleTime: 0 } });
  const objectType = getObjectTypeBySlug(objectTypes, slug ?? '');
  const rootHref = router.buildLocation({ to: '/workspaces/$workspaceId/objects', params: { workspaceId } }).href;

  useRegisterBreadcrumbs([
    { id: 'objects', text: 'Custom objects', href: rootHref },
    ...(objectType == null
      ? []
      : [
          {
            id: `objects.${objectType.id}`,
            text: objectType.name,
            titleText: `Object type ${objectType.id}`,
            className: 'fs-mask',
            isTruncated: true,
            href: router.buildLocation({
              to: '/workspaces/$workspaceId/objects/$slug',
              params: { workspaceId, slug: objectType.slug },
            }).href,
          },
        ]),
  ]);

  if (objectType == null) {
    return <SafeNavigate to={rootHref} options={{ shouldReplaceHistoryEntry: true }} />;
  }
  if (slug !== objectType.slug) {
    const href = router.buildLocation({
      to: route,
      params: { workspaceId, slug: objectType.slug },
    }).href;
    return (
      <SafeNavigate
        to={objectTypeRedirectHref(href, location.searchStr, location.hash)}
        options={{ shouldReplaceHistoryEntry: true }}
      />
    );
  }

  return <>{children(objectType, workspaceId)}</>;
}
